import type { Data } from '@puckeditor/core'
import { Render } from '@puckeditor/core'
import * as React from 'react'

import {
  type PublishedChromeConfig,
  hasRenderablePuckContent,
} from '@/lib/puck-data-utils'
import { BrandingOverrideProvider } from '@/hooks/use-branding'

import GeneralV1PageFrame from '@/components/homepage/non-fsds-general-v1/general-v1-page-frame'
import HomepageUnavailableState from '@/components/homepage/non-fsds-general-v1/homepage-unavailable-state'
import type { LandingInstructor } from '@/components/homepage/non-fsds-landing-content'
import NonFsdsLandingPage from '@/components/homepage/non-fsds-landing-page'
import Footer from '@/components/layouts/footer'
import NavBar from '@/components/layouts/nav/navbar'

import {
  getNonFsdsFooterBrandingOverride,
  getNonFsdsHeaderBrandingOverride,
  getNonFsdsHeaderConfig,
  NON_FSDS_FOOTER_CONFIG,
} from '@/configs/non-fsds-config'
import { puckConfig } from '@/configs/puck-config'

interface NonFsdsHomepageWithTemplateProps {
  lang: string
  isCourseEnrolled: boolean
  instructorsList: LandingInstructor[]
  initialPuckData?: Data | null
  /** Header/footer chrome extracted before editor-only blocks were stripped (SSR). */
  initialPublishedChrome?: PublishedChromeConfig | null
  /** When true, fall back to the legacy static NonFsdsLandingPage instead of the unavailable state. */
  useLegacyFallback?: boolean
}
/**
 * Non-FSDS homepage that renders Puck template content, a branded
 * unavailable state, or (via kill-switch) the legacy static fallback.
 *
 * Data is resolved server-side via getServerSideProps in pages/index.tsx.
 * If Puck data contains non-FSDS content → render via <Render>.
 * Otherwise → render <HomepageUnavailableState> (or <NonFsdsLandingPage>
 * when useLegacyFallback is true during the compatibility window).
 */
export default function NonFsdsHomepageWithTemplate({
  lang,
  isCourseEnrolled,
  instructorsList,
  initialPuckData,
  initialPublishedChrome,
  useLegacyFallback = false,
}: NonFsdsHomepageWithTemplateProps) {
  const isServerPrefetched = initialPuckData !== undefined

  const [puckData] = React.useState<Data | null>(isServerPrefetched ? (initialPuckData ?? null) : null)
  const [publishedChrome] = React.useState<PublishedChromeConfig | null>(
    isServerPrefetched ? (initialPublishedChrome ?? null) : null
  )

  // No Puck data or only header/footer chrome → unavailable state (or legacy fallback via kill switch)
  if (!puckData || !hasRenderablePuckContent(puckData)) {
    if (useLegacyFallback) {
      return <NonFsdsLandingPage lang={lang} isCourseEnrolled={isCourseEnrolled} instructorsList={instructorsList} />
    }
    return <HomepageUnavailableState />
  }

  // Render Puck content with its own chrome (header/footer) from the template data
  const chrome = publishedChrome ?? {}
  const headerBranding = getNonFsdsHeaderBrandingOverride(chrome.header?.brandingOverride)
  const footerBranding = getNonFsdsFooterBrandingOverride(chrome.footer?.brandingOverride)
  const headerConfig = getNonFsdsHeaderConfig(chrome.header?.headerConfig)
  const footerConfig = { ...NON_FSDS_FOOTER_CONFIG, ...(chrome.footer?.footerConfig ?? {}) }
  const showHeader = chrome.header?.visible !== false
  const showFooter = chrome.footer?.visible !== false

  // Detect general-v1 template from Puck content component types
  const isGeneralV1 = puckData.content?.some(
    (item: { type?: string }) => item.type?.startsWith('GeneralV1')
  )

  // For non-General-V1, apply shared navbar with overlay preset
  const resolvedHeaderConfig = isGeneralV1
    ? { ...headerConfig, templatePreset: 'general-v1-overlay' }
    : headerConfig

  const puckContent = <Render config={puckConfig} data={puckData} />

  if (isGeneralV1) {
    return (
      <GeneralV1PageFrame
        lang={lang === 'vi' ? 'VIE' : 'ENG'}
        showHeader={showHeader}
        showFooter={showFooter}
      >
        {puckContent}
      </GeneralV1PageFrame>
    )
  }

  return (
    <div>
      {showHeader && (
          <BrandingOverrideProvider value={headerBranding}>
            <NavBar headerConfig={resolvedHeaderConfig} />
          </BrandingOverrideProvider>
      )}

      {puckContent}

      {showFooter && (
          <BrandingOverrideProvider value={footerBranding}>
            <Footer footerConfig={footerConfig} />
          </BrandingOverrideProvider>
      )}
    </div>
  )
}
